import { AlertTriangle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EditModal, SummaryCard, type EditModalControls } from '@nucleus-commerce/admin-kit/forms';
import type { SeoWarning } from '@nucleus-commerce/api-client/admin';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

export type SearchEnginesTab = 'listing' | 'answers';

export interface SearchEnginesCardProps {
  /** Beside the title: a Shared or This store chip. */
  badge?: ReactNode;
  /** The far end of the title row: Edit, Edit on master. */
  action?: ReactNode;
  /** From `useEditModal` over the fields of both tabs. */
  modal: EditModalControls['modal'];
  /** The Search listing tab. */
  listing: ReactNode;
  /** The Summary and FAQ tab. */
  answers: ReactNode;
  /** Beside the Summary and FAQ tab's name: "4 suggested". */
  answersBadge?: ReactNode;
  /** The tab holding a field error, so the modal never hides one behind the other tab. */
  errorTab?: SearchEnginesTab | undefined;
  /** The summary on the card. */
  children: ReactNode;
}

function SearchEnginesTabs({
  listing,
  answers,
  answersBadge,
  errorTab,
}: Pick<SearchEnginesCardProps, 'listing' | 'answers' | 'answersBadge' | 'errorTab'>) {
  const { t } = useTranslation('products');
  const [tab, setTab] = useState<string>(errorTab ?? 'listing');
  const [seenError, setSeenError] = useState(errorTab);
  if (errorTab !== seenError) {
    setSeenError(errorTab);
    if (errorTab) setTab(errorTab);
  }
  return (
    <Tabs value={tab} onValueChange={setTab} className="gap-4">
      <TabsList variant="line" className="bg-background sticky top-0 z-10 w-full justify-start">
        <TabsTrigger value="listing" className="flex-none">
          {t('searchCard.tabListing')}
        </TabsTrigger>
        <TabsTrigger value="answers" className="flex-none">
          {t('searchCard.tabAnswers')}
          {answersBadge}
        </TabsTrigger>
      </TabsList>
      {/* Both stay mounted: Done validates the fields of both, and a tab must not drop them. */}
      <TabsContent value="listing" forceMount className="data-[state=inactive]:hidden">
        {listing}
      </TabsContent>
      <TabsContent value="answers" forceMount className="data-[state=inactive]:hidden">
        {answers}
      </TabsContent>
    </Tabs>
  );
}

/**
 * The Search and answer engines card of both product pages: a summary, and one modal with the
 * listing beside its preview on one tab and the summary and FAQ on the other. The host supplies
 * the fields, because each page binds them to a form of its own.
 */
export function SearchEnginesCard({
  badge,
  action,
  modal,
  listing,
  answers,
  answersBadge,
  errorTab,
  children,
}: SearchEnginesCardProps) {
  const { t } = useTranslation('products');
  return (
    <SummaryCard title={t('searchCard.title')} badge={badge} action={action}>
      {children}
      <EditModal
        {...modal}
        size="xl"
        title={t('searchCard.title')}
        description={t('searchCard.description')}
      >
        <SearchEnginesTabs
          listing={listing}
          answers={answers}
          answersBadge={answersBadge}
          errorTab={errorTab}
        />
      </EditModal>
    </SummaryCard>
  );
}

export interface SearchListingSummaryProps {
  path: string;
  title: string;
  description: string | null;
  warnings: readonly SeoWarning[];
  /** The line under it: what the modal holds. */
  note?: ReactNode;
}

/** The card's summary on both pages: the listing as a search result, then what needs attention. */
export function SearchListingSummary({
  path,
  title,
  description,
  warnings,
  note,
}: SearchListingSummaryProps) {
  const { t } = useTranslation(['products', 'seo']);
  return (
    <>
      <div className="bg-muted/40 flex flex-col gap-0.5 rounded-lg border px-3 py-2.5">
        <span className="text-muted-foreground truncate font-mono text-[11px]">{path}</span>
        <span className="text-primary text-[15px] leading-snug break-words">
          {title || t('seo:preview.untitled')}
        </span>
        <span className="text-muted-foreground line-clamp-2 text-[13px] break-words">
          {description ?? t('seo:preview.noDescription')}
        </span>
      </div>
      {warnings.length > 0 ? (
        <p className="flex items-start gap-1.5 text-[13px]">
          <AlertTriangle className="text-warning mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>{warnings.map((warning) => t(`seo:warning.${warning}`)).join(' · ')}</span>
        </p>
      ) : null}
      {note ? <p className="text-muted-foreground text-[13px]">{note}</p> : null}
    </>
  );
}
